<template>
  <div class="max-w-4xl mx-auto space-y-6 pb-16">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
        {{ $t('AI_ONBOARDING.QUESTIONS.TITLE') || 'Cuộc trò chuyện 5 phút' }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.QUESTIONS.SUBTITLE') || 'Bot muốn hiểu về business của bạn. Trả lời thoải mái nhé!' }}
      </p>
      
      <!-- Progress -->
      <div class="flex items-center justify-center gap-2 mt-4">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ answeredCount }}/{{ totalQuestions }} {{ $t('AI_ONBOARDING.QUESTIONS.ANSWERED') || 'đã trả lời' }}
        </span>
        <div class="w-32 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div class="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300" :style="{ width: `${progressPercent}%` }"></div>
        </div>
      </div>
    </div>

    <!-- Loading Questions -->
    <div v-if="isLoadingQuestions" class="text-center py-12">
      <div class="inline-flex flex-col items-center gap-4">
        <lucide-icon name="loader-2" :size="32" class="animate-spin text-slate-500 dark:text-slate-400" />
        <div class="space-y-2">
          <p class="text-base font-medium text-slate-700 dark:text-slate-300">
            {{ $t('AI_ONBOARDING.QUESTIONS.LOADING_TITLE') || 'AI đang tạo câu hỏi phù hợp với bạn...' }}
          </p>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            {{ $t('AI_ONBOARDING.QUESTIONS.LOADING_TEXT') || 'Vui lòng đợi trong giây lát' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Questions -->
    <div v-else class="space-y-5">
      <div
        v-for="(question, idx) in questions"
        :key="idx"
        class="p-6 bg-white dark:bg-slate-800 rounded-2xl border-2"
        :class="isQuestionAnswered(idx, question) ? 'border-green-500 shadow-md' : 'border-slate-200 dark:border-slate-700'"
      >
        <div class="flex items-start gap-3">
          <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-hilab to-hilab-dark">
            <span class="text-white font-bold">{{ idx + 1 }}</span>
          </div>
          <div class="flex-1 space-y-3">
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ questionText(question) }}
              </h3>
              <div class="mt-2 flex flex-wrap gap-2 text-xs">
                <span class="px-2 py-1 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200 border border-purple-100 dark:border-purple-700">
                  {{ questionTypeLabel(question) }}
                </span>
                <span class="px-2 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-100 dark:border-blue-800">
                  {{ questionCategoryLabel(question) }}
                </span>
              </div>
            </div>
            <div
              v-if="choicesForQuestion(idx, question).length"
              class="grid gap-2 sm:grid-cols-2"
            >
              <label
                v-for="option in choicesForQuestion(idx, question)"
                :key="option.value"
                class="flex items-start gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-500 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="mt-1 accent-purple-600"
                  :checked="isChoiceSelected(idx, option.value)"
                  @change="toggleChoice(idx, option.value)"
                />
                <span class="text-sm text-slate-700 dark:text-slate-200">
                  <strong class="mr-1">{{ option.label }}.</strong>{{ option.text }}
                </span>
              </label>
            </div>
            <textarea
              v-if="!isMultiChoiceQuestion(question)"
              v-model="answers[`question_${idx}`]"
              :placeholder="$t('AI_ONBOARDING.QUESTIONS.ANSWER_PLACEHOLDER') || 'Nhập câu trả lời của bạn...'"
              rows="4"
              class="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 text-sm resize-y min-h-[120px]"
            ></textarea>
            <div class="flex items-center gap-2 text-xs">
              <lucide-icon v-if="isQuestionAnswered(idx, question)" name="check-circle" :size="14" class="text-green-500" />
              <span :class="isQuestionAnswered(idx, question) ? 'text-green-600 dark:text-green-400 font-medium' : 'text-slate-500'">
                {{ isQuestionAnswered(idx, question) ? $t('AI_ONBOARDING.QUESTIONS.COMPLETED') || '✓ Đã trả lời' : $t('AI_ONBOARDING.QUESTIONS.WAITING') || 'Chưa trả lời' }}
              </span>
              <span v-if="answers[`question_${idx}`] && typeof answers[`question_${idx}`] === 'string' && answers[`question_${idx}`].trim().length > 0" class="text-slate-400">
                • {{ (answers[`question_${idx}`] || '').trim().length }} {{ $t('GENERAL.CHARACTERS') || 'ký tự' }}
              </span>
              <span v-else-if="isMultiChoiceQuestion(question) && (choiceSelections[idx] || []).length > 0" class="text-slate-400">
                • {{ (choiceSelections[idx] || []).length }} {{ $t('AI_ONBOARDING.QUESTIONS.SELECTED') || 'lựa chọn' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Minimum Requirement Notice -->
    <div v-if="answeredCount < minRequired && !isLoadingQuestions" class="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl">
      <div class="flex items-start gap-3">
        <lucide-icon name="info" :size="20" class="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div>
          <p class="text-sm font-medium text-blue-900 dark:text-blue-100">
            {{ $t('AI_ONBOARDING.QUESTIONS.MIN_REQUIRED_TITLE') || 'Tiếp tục trả lời để bot hiểu rõ hơn về bạn' }}
          </p>
          <p class="text-xs text-blue-700 dark:text-blue-300 mt-1">
            {{ $t('AI_ONBOARDING.QUESTIONS.MIN_REQUIRED_TEXT') || 'Trả lời càng nhiều câu hỏi, bot sẽ càng thông minh và chính xác hơn!' }}
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

export default {
  name: 'AiConversationalQuestionsStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'back'],

  data() {
    return {
      answers: {},
      choiceSelections: {},
      questions: [],
      isLoadingQuestions: false,
      totalQuestions: 0,
      minRequired: 3,
    };
  },

  computed: {
    ...mapState('aiOnboarding', {
      conversationalAnswers: state => state.conversationalAnswers,
      questionBank: state => state.questionBank,
      botProfile: state => state.botProfile,
      botModes: state => state.botModes,
      providerSetup: state => state.providerSetup,
    }),
    
    answeredCount() {
      // Đếm câu đã trả lời (text >= 3 ký tự hoặc đã chọn checkbox)
      return this.questions.reduce((count, _q, idx) => {
        const text = this.answers[`question_${idx}`];
        const choices = this.choiceSelections[idx] || [];
        const hasText = typeof text === 'string' && text.trim().length >= 3;
        const hasChoice = choices.length > 0;
        return count + (hasText || hasChoice ? 1 : 0);
      }, 0);
    },
    
    progressPercent() {
      return this.totalQuestions > 0 ? Math.round((this.answeredCount / this.totalQuestions) * 100) : 0;
    },
    
    canProceed() {
      // Phải có ít nhất minRequired câu trả lời (text tối thiểu hoặc tick checkbox)
      // Và phải có questions đã được load
      return this.questions.length > 0 && this.answeredCount >= this.minRequired;
    },
  },

  async mounted() {
    // Load from store if exists
    if (this.conversationalAnswers && Object.keys(this.conversationalAnswers).length > 0) {
      // Kiểm tra xem có phải là answers từ questions không
      const hasQuestionAnswers = Object.keys(this.conversationalAnswers).some(key => key.startsWith('question_'));
      if (hasQuestionAnswers) {
        this.answers = { ...this.conversationalAnswers };
        Object.entries(this.conversationalAnswers).forEach(([key, value]) => {
          if (key.endsWith('_choices') && Array.isArray(value)) {
            const idx = Number(key.replace('question_', '').replace('_choices', ''));
            if (!Number.isNaN(idx)) {
              this.choiceSelections[idx] = value;
            }
          }
        });
      }
    }
    
    // Dùng questionBank nếu đã có, tránh gọi lại AI
    if (this.questionBank && this.questionBank.length) {
      this.questions = [...this.questionBank];
      this.totalQuestions = this.questions.length;
      this.questions.forEach((q, idx) => {
        if (!this.choiceSelections[idx]) this.choiceSelections[idx] = [];
        const key = `question_${idx}`;
        if (!this.answers[key]) this.answers[key] = '';
      });
    } else {
      // Generate questions bằng AI nếu chưa có
      if (this.questions.length === 0) {
        await this.generateQuestions();
      }
    }
    
    // Auto-save every 5 seconds
    this.autoSaveInterval = setInterval(() => {
      if (this.answeredCount > 0) {
        const payload = this.buildAnswersPayload();
        this.updateConversationalAnswers(payload);
      }
    }, 5000);
  },

  beforeUnmount() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
  },

  methods: {
    ...mapActions('aiOnboarding', ['updateConversationalAnswers']),

    isMultiChoiceQuestion(question) {
      if (!question || typeof question !== 'object') return false;
      const hasOptions = Array.isArray(question.options) && question.options.length > 0;
      return (question.type === 'multi_choice' || question.type === 'MULTI_CHOICE') && hasOptions;
    },

    choicesForQuestion(idx, question) {
      if (!question || !Array.isArray(question.options) || !question.options.length) {
        return [];
      }

      return question.options.map((opt, optionIdx) => ({
        label: opt.label || String.fromCharCode(65 + optionIdx),
        value: opt.value || `option_${optionIdx}`,
        text: opt.text || opt.label || opt.value || '',
      }));
    },

    isChoiceSelected(idx, value) {
      return (this.choiceSelections[idx] || []).includes(value);
    },

    isQuestionAnswered(idx, question) {
      const text = this.answers[`question_${idx}`];
      const choices = this.choiceSelections[idx] || [];
      const hasText = typeof text === 'string' && text.trim().length >= 3;
      const hasChoice = choices.length > 0;
      return hasText || hasChoice;
    },

    toggleChoice(idx, value) {
      const current = this.choiceSelections[idx] || [];
      if (current.includes(value)) {
        this.choiceSelections[idx] = current.filter(v => v !== value);
      } else {
        this.choiceSelections[idx] = [...current, value];
      }
    },
    
    async generateQuestions() {
      this.isLoadingQuestions = true;
      try {
        const response = await this.$store.dispatch('aiOnboarding/generateQuestions', {
          bot_profile: this.botProfile,
          bot_modes: this.botModes,
          provider_setup: this.providerSetup || { credentialMode: 'system' },
        });
        
        if (response && response.data && response.data.success && response.data.questions) {
          this.questions = response.data.questions.map((q, idx) => {
            if (typeof q === 'string') {
              return { question: q, type: 'open_text', category: 'faqs', options: [], key: `q_${idx}` };
            }
            return {
              question: q.question || q.text || '',
              type: q.type || 'open_text',
              category: q.category || 'faqs',
              options: Array.isArray(q.options) ? q.options : [],
              key: q.key || `q_${idx}`,
            };
          });
          this.totalQuestions = this.questions.length;
          
          // Khởi tạo answers object với keys từ questions
          const newAnswers = { ...this.answers };
          this.questions.forEach((q, idx) => {
            const key = `question_${idx}`;
            if (!newAnswers[key]) {
              newAnswers[key] = '';
            }
            if (!this.choiceSelections[idx]) {
              this.choiceSelections[idx] = [];
            }
          });
          this.answers = newAnswers;
        } else {
          throw new Error('Invalid response from API');
        }
      } catch (error) {
        console.error('Failed to generate questions:', error);
        this.$store.commit('aiOnboarding/AI_ONBOARDING_SET_UI_FLAG', {
          error: this.$t('AI_ONBOARDING.QUESTIONS.GENERATE_ERROR') || 'Không thể tạo câu hỏi. Vui lòng thử lại.',
        });
        // Không fallback, để user thấy lỗi và retry
        this.questions = [];
        this.totalQuestions = 0;
      } finally {
        this.isLoadingQuestions = false;
      }
    },

    getCanProceed() {
      // Luôn trả về true để nút không bị disable, validation sẽ được xử lý trong handleNext
      return true;
    },

    async handleNext() {
      if (!this.canProceed) {
        const remaining = this.minRequired - this.answeredCount;
        const message = this.$t('AI_ONBOARDING.QUESTIONS.MIN_ERROR', { 
          count: this.minRequired, 
          remaining 
        }) || 
          `Vui lòng trả lời ít nhất ${this.minRequired} câu hỏi. Bạn còn thiếu ${remaining} câu.`;
        useAlert(message, { type: 'alert' });
        return;
      }

      const payload = this.buildAnswersPayload();
      await this.updateConversationalAnswers(payload);
      this.$emit('next');
    },

    buildAnswersPayload() {
      const payload = { ...this.answers };
      Object.entries(this.choiceSelections).forEach(([idx, values]) => {
        payload[`question_${idx}_choices`] = values;
      });
      return payload;
    },

    questionText(question) {
      if (!question) return '';
      if (typeof question === 'string') return question;
      return question.question || question.text || '';
    },

    questionTypeLabel(question) {
      const type = typeof question === 'object' ? question.type : null;
      if (type === 'multi_choice') return this.$t('AI_ONBOARDING.QUESTIONS.TYPE_MULTI') || 'Chọn nhanh';
      return this.$t('AI_ONBOARDING.QUESTIONS.TYPE_OPEN') || 'Trả lời mở';
    },

    questionCategoryLabel(question) {
      const category = typeof question === 'object' ? question.category : null;
      const map = {
        products: this.$t('AI_ONBOARDING.QUESTIONS.CAT_PRODUCTS') || 'Sản phẩm/Dịch vụ',
        faqs: this.$t('AI_ONBOARDING.QUESTIONS.CAT_FAQ') || 'FAQs',
        rules: this.$t('AI_ONBOARDING.QUESTIONS.CAT_RULES') || 'Quy tắc',
        tone: this.$t('AI_ONBOARDING.QUESTIONS.CAT_TONE') || 'Phong cách',
      };
      return map[category] || (this.$t('AI_ONBOARDING.QUESTIONS.CAT_GENERIC') || 'Thông tin');
    },
  },
};
</script>
